import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { Theme, ThemeDetails } from '@yandex-int/k-common/typings';
import { LessonInfo } from 'platform-components';
import { DEFAULT_PAGE_TO_LOAD } from 'platform-components/constants';
import { getIsContentManager } from 'platform-components/src/components/user/selectors';
import { InjectedProps as WithRouterPathProps, withRouterPath } from 'platform-components/hocs';
import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { MetrikaParams } from 'common.components/hooks/use-prep-lesson-click';
import { mapLessonsToIds } from 'common.components/lesson/utils';
import withTime, { InjectedProps as WithTimeProps } from 'common.components/timer/withTime';
import { prepLessonsActions } from 'store/prep-lessons/actions';
import { useLoadPrepLessonsDetails } from 'store/prep-lessons/hooks/use-load-prep-lessons-details';
import {
    getPrepLessonsLoadingStatus,
    getPrepLessonsDetails,
    getPrepLessonsDetailsLoadingStatus,
    getPrepLessonsWithDetails,
} from 'store/prep-lessons/selectors';
import { PrepLessonDetailsClientSide } from 'store/prep-lessons/typings';

// @ts-ignore
import { ReduxTeacherStore, RootAction } from '../../../typings';
import * as storeActions from '../../library.store/actions';
import * as storeSelectors from '../../library.store/selectors';

import PromoThemeView from './promo__theme.view';

interface HOCProps extends WithTimeProps, WithRouterPathProps {}

interface OwnProps {
    theme: Theme;
    promoThemeId?: number;
    scrollToTheme?: (themeRef: React.RefObject<HTMLDivElement>) => void;
    metrikaParams: MetrikaParams;
}

interface StoreProps {
    themeDetails: ThemeDetails | null;
    themeDetailsLoadingStatus: LOADING_STATUSES;
    courseId: number | null;
    prepLessons: LessonInfo[];
    prepLessonsIds: number[];
    prepLessonsLoadingStatus: LOADING_STATUSES;
    prepLessonsDetails: Record<number, PrepLessonDetailsClientSide>;
    prepLessonsDetailsLoadingStatus: LOADING_STATUSES;
    isContentManager: boolean;
}

interface DispatchProps {
    loadPrepLessonsRequest: typeof prepLessonsActions.loadPrepLessonsRequest;
    loadPrepLessonsDetailsRequest: typeof prepLessonsActions.loadPrepLessonsDetailsRequest;
    getThemeDetailsRequest: typeof storeActions.getThemeDetailsRequest;
}

interface Props extends StoreProps, DispatchProps, OwnProps, HOCProps {}

const PromoTheme = (props: Props) => {
    const {
        loadPrepLessonsRequest,
        loadPrepLessonsDetailsRequest,
        getThemeDetailsRequest,

        theme,
        themeDetails,
        themeDetailsLoadingStatus,
        promoThemeId,
        scrollToTheme,
        prepLessons,
        prepLessonsIds,
        courseId,
        prepLessonsDetails,
        prepLessonsLoadingStatus,
        now,
        isContentManager,
        prepLessonsDetailsLoadingStatus,
        metrikaParams,
    } = props;
    const { id: themeId } = theme;

    const loadPrepLessons = useCallback(() => {
        const hasEmptyPrepLessons = prepLessons.length === 0 && prepLessonsLoadingStatus === LOADING_STATUSES.SUCCESS;
        const shouldLoadPrepLessons =
            (!prepLessons || prepLessons.length === 0) &&
            prepLessonsLoadingStatus !== LOADING_STATUSES.LOADING &&
            prepLessonsLoadingStatus !== LOADING_STATUSES.ERROR;

        if (!hasEmptyPrepLessons && shouldLoadPrepLessons) {
            loadPrepLessonsRequest({ themeId, page: DEFAULT_PAGE_TO_LOAD, pageSize: 1000 });
        }
    }, [loadPrepLessonsRequest, prepLessons, prepLessonsLoadingStatus, themeId]);

    const getThemeDetails = useCallback(() => {
        const shouldLoad =
            themeDetailsLoadingStatus !== LOADING_STATUSES.LOADING &&
            themeDetailsLoadingStatus !== LOADING_STATUSES.ERROR;

        if (themeId && !themeDetails && shouldLoad) {
            getThemeDetailsRequest(themeId);
        }
    }, [getThemeDetailsRequest, themeId, themeDetails, themeDetailsLoadingStatus]);

    useEffect(() => {
        loadPrepLessons();
        getThemeDetails();
    }, [
        themeId,
        prepLessons,
        prepLessonsLoadingStatus,
        loadPrepLessonsRequest,
        getThemeDetailsRequest,
        loadPrepLessons,
        getThemeDetails,
    ]);

    useLoadPrepLessonsDetails({
        courseId,
        prepLessonsIds,
        prepLessonsDetails,
        loadPrepLessonsDetailsRequest,
        prepLessonsDetailsLoadingStatus,
    });

    return (
        <PromoThemeView
            loadingStatus={prepLessonsLoadingStatus}
            metrikaParams={metrikaParams}
            now={now}
            prepLessons={prepLessons}
            prepLessonsDetailsLoadingStatus={prepLessonsDetailsLoadingStatus}
            promoThemeId={promoThemeId}
            scrollToTheme={scrollToTheme}
            shouldShowId={isContentManager}
            theme={theme}
            themeDetails={themeDetails}
            themeDetailsLoadingStatus={themeDetailsLoadingStatus}
        />
    );
};

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: OwnProps & HOCProps): StoreProps => {
    const themeId = ownProps.theme.id;
    const courseId = getCurrentYearCourseId(storeState);
    const prepLessons = getPrepLessonsWithDetails(storeState, {
        themeId,
        courseId,
    });
    const prepLessonsIds = mapLessonsToIds(prepLessons);

    return {
        courseId,
        prepLessons,
        prepLessonsIds,
        isContentManager: getIsContentManager(storeState),
        themeDetails: storeSelectors.getThemeDetails(storeState, { themeId }),
        themeDetailsLoadingStatus: storeSelectors.getThemeDetailsLoadingStatus(storeState, { themeId }),
        prepLessonsDetails: getPrepLessonsDetails(storeState, { courseId }),
        prepLessonsLoadingStatus: getPrepLessonsLoadingStatus(storeState, { themeId }),
        prepLessonsDetailsLoadingStatus: getPrepLessonsDetailsLoadingStatus(storeState, {
            courseId,
            lessonsIds: prepLessonsIds,
        }),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>): DispatchProps => {
    return bindActionCreators(
        {
            loadPrepLessonsRequest: prepLessonsActions.loadPrepLessonsRequest,
            loadPrepLessonsDetailsRequest: prepLessonsActions.loadPrepLessonsDetailsRequest,
            getThemeDetailsRequest: storeActions.getThemeDetailsRequest,
        },
        dispatch
    );
};

export default withRouterPath(withTime(connect(mapStateToProps, mapDispatchToProps)(PromoTheme)));
